import { useRef, memo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { IdealCustomerProfile } from '../../types';
import { useCarousel } from '../../hooks/suggestionWizard';
import { ICPCard } from './ICPCard';

interface ICPCarouselProps {
  icps: IdealCustomerProfile[];
  expandedDescriptionId: string | null;
  onExpandDescription: (id: string | null) => void;
  onSelectIcp: (icp: IdealCustomerProfile) => void;
}

/**
 * Horizontal carousel for displaying ICP cards.
 * Supports navigation arrows, dot indicators, and snap scrolling.
 */
export const ICPCarousel = memo<ICPCarouselProps>(({
  icps,
  expandedDescriptionId,
  onExpandDescription,
  onSelectIcp,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { activeIndex, scrollToIndex, handleScroll, canScrollLeft, canScrollRight } = useCarousel({
    containerRef,
    totalItems: icps.length,
  });

  return (
    <div className="relative">
      {/* Navigation Arrows */}
      {icps.length > 1 && (
        <>
          {/* Left Arrow */}
          <button
            type="button"
            onClick={() => scrollToIndex(activeIndex - 1)}
            disabled={!canScrollLeft}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white border border-gray-200 shadow-md flex items-center justify-center transition-all ${
              canScrollLeft
                ? 'opacity-100 hover:bg-gray-50 hover:border-gray-300'
                : 'opacity-0'
            }`}
            style={{ marginLeft: '-4px' }}
            aria-label="Previous profile"
          >
            <ChevronLeft className="w-4 h-4 text-gray-600" />
          </button>

          {/* Right Arrow */}
          <button
            type="button"
            onClick={() => scrollToIndex(activeIndex + 1)}
            disabled={!canScrollRight}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white border border-gray-200 shadow-md flex items-center justify-center transition-all ${
              canScrollRight
                ? 'opacity-100 hover:bg-gray-50 hover:border-gray-300'
                : 'opacity-0'
            }`}
            style={{ marginRight: '-4px' }}
            aria-label="Next profile"
          >
            <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>
        </>
      )}

      {/* Carousel Container */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex gap-3 overflow-x-auto pb-3 snap-x snap-mandatory scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {icps.map((icp, index) => (
          <ICPCard
            key={icp.id}
            icp={icp}
            index={index}
            totalCount={icps.length}
            isDescExpanded={expandedDescriptionId === icp.id}
            onExpandToggle={() => onExpandDescription(expandedDescriptionId === icp.id ? null : icp.id)}
            onSelect={() => onSelectIcp(icp)}
          />
        ))}
      </div>

      {/* Dot Navigator */}
      {icps.length > 1 && (
        <div className="flex justify-center items-center gap-2 mt-2">
          {icps.map((_, idx) => (
            <button
              key={idx}
              onClick={() => scrollToIndex(idx)}
              className={`transition-all duration-200 rounded-full ${
                idx === activeIndex
                  ? 'w-6 h-2 bg-gray-900'
                  : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to profile ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
});

ICPCarousel.displayName = 'ICPCarousel';

export default ICPCarousel;
